import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MessageSquare, Users, GraduationCap, UserCheck, Send, ArrowLeft, Bell, Mail, Smartphone, Clock, CheckCircle, History, Eye, Search, ChevronDown, Users as UsersIcon, FileText, Download } from "lucide-react";

import ContentLoader from "../authentication/ContentLoader";

import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import SuspensionWarning from "./SuspensionWarning";
import useSuspensionCheck from "./useSuspensionCheck";
import { db, auth } from "../firebase/config";
import { collection, query, where, orderBy, getDocs, addDoc, updateDoc, doc, serverTimestamp, onSnapshot, limit, writeBatch, arrayUnion } from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from 'sanitize-html';

interface CommunicationCategory {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  recipientCount: number;
  color: string;
  recipients: string;
  disabled?: boolean;
}

interface Memo {
  id: string;
  title: string;
  description: string;
  fileUrl: string;
  fileName: string;
  fileType: string;
  targetAudience: 'staff' | 'teacher-admin' | 'student' | 'parent' | 'everyone';
  createdAt: string;
  senderEmail: string;
  recipientCount: number;
  readBy: string[];
}

interface Conversation {
  id: string;
  title: string;
  allowedEmails: string[];
  participantRoles: string[];
  type: '1:1' | 'group';
  lastMessage: {
    content: string;
    senderEmail: string;
    timestamp: string;
  } | null;
  lastUpdatedAt: string;
  recipients: string;
  recipientCount: number;
  hasReceivedMessages: boolean;
  schoolID: string;
  unreadCount: number;
}

interface Message {
  id: string;
  content: string;
  senderEmail: string;
  timestamp: string;
  readBy: string[];
  priority: string;
  deliveryMethod: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  subject?: string;
  studentName?: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
  role?: string;
}

interface StudentData {
  registrationNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  guardians: { name: string; phone: string; email: string; address: string }[];
  status: string;
  studentUid: string;
}

const TeacherCommunication: React.FC<{ userRole?: string }> = ({ }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Check suspension status
  const { isSuspended, isLoading: suspensionLoading, schoolName: suspendedSchoolName } = useSuspensionCheck();
  
  const [activeView, setActiveView] = useState<string>("overview");
  const [selectedCategory, setSelectedCategory] = useState<CommunicationCategory | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [deliveryMethod, setDeliveryMethod] = useState("app");
  const [schoolID, setSchoolID] = useState<string>("");
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [guardians, setGuardians] = useState<User[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState("");
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientType, setRecipientType] = useState<"teacher" | "guardian" | "student" | null>(null);
  const [returnView, setReturnView] = useState<"received-messages" | "sent-messages">("received-messages");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  
  // Memo States
  const [activeTab, setActiveTab] = useState<"messages" | "memos">("messages");
  const [selectedMemo, setSelectedMemo] = useState<Memo | null>(null);
  const [activeMemoCategory, setActiveMemoCategory] = useState<string | null>(null);
  const [memos, setMemos] = useState<Memo[]>([]);
  const [memoCategories] = useState<CommunicationCategory[]>([
    { id: "staff", title: "Staff Memos", description: "Internal Staff Communication", icon: GraduationCap, recipientCount: 0, color: "bg-green-500", recipients: "Teaching & non-teaching staff" },
    { id: "teacher-admin", title: "Teacher–Admin Memos", description: "Between administration and teachers", icon: GraduationCap, recipientCount: 0, color: "bg-blue-500", recipients: "Teachers and Administration" },
    { id: "everyone", title: "Whole-School Memos", description: "General Notices for everyone", icon: Users, recipientCount: 0, color: "bg-gray-500", recipients: "Staff, Students, and Parents" },
  ]);

  const [communicationCategories, setCommunicationCategories] = useState<CommunicationCategory[]>([
    { id: "staff", title: "Communicate with School Staff", description: "Internal messages for teachers and staff", icon: GraduationCap, recipientCount: 0, color: "bg-green-500", recipients: "Teachers, administrators, and support staff" },
    { id: "guardians", title: "Communicate with Guardians", description: "Updates and notices for parents/guardians", icon: UserCheck, recipientCount: 0, color: "bg-purple-500", recipients: "Parents and legal guardians" },
    { id: "students", title: "Communicate with Students", description: "Direct messages to student body", icon: Users, recipientCount: 0, color: "bg-orange-500", recipients: "All enrolled students" },
  ]);

  const getMessagePreview = (content: string, maxLength: number) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const getSenderName = (email: string) => {
    if (!email) return "Unknown Sender";
    if (email === auth.currentUser?.email) return "Me";
    return teachers.find(t => t.email === email)?.name || 
           guardians.find(g => g.email === email)?.name || 
           students.find(s => s.email === email)?.name || 
           email;
  };

  const getConversationListTitle = (conv: Conversation) => {
    if (conv.type === '1:1') {
      const otherEmail = conv.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        return getSenderName(otherEmail);
      }
    }
    return conv.title;
  };

  const getDisplayTitle = () => {
    if (selectedConversation?.type === '1:1') {
      const otherEmail = selectedConversation.allowedEmails.find(e => e !== auth.currentUser?.email);
      if (otherEmail) {
        const otherName = getSenderName(otherEmail);
        return returnView === "received-messages" ? `From ${otherName}` : `To ${otherName}`;
      }
    }
    return selectedConversation?.title || '';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idTokenResult = await getIdTokenResult(user, true);
          const claims = idTokenResult.claims as CustomClaims;
          const affiliations = claims.affiliations || [];
          const defaultAff = claims.defaultAffiliation;
          const claimSchoolID = claims.schoolID;

          const teacherAff = defaultAff?.role === "school_staff" && defaultAff.schoolID
            ? defaultAff
            : affiliations.find(aff => aff.role === "school_staff" && aff.schoolID);

          if (teacherAff?.schoolID && teacherAff.schoolID === claimSchoolID) {
            setSchoolID(teacherAff.schoolID);
            setHasValidClaims(true);
          } else {
            throw new Error("Invalid school staff claims");
          }
        } catch (error: any) {
          console.error("Error verifying user claims:", error);
          // Check if it's a network error
          const isNetworkError = error?.code === 'auth/network-request-failed' ||
                                error?.message?.includes('ERR_NAME_NOT_RESOLVED') ||
                                error?.message?.includes('network-request-failed') ||
                                error?.message?.includes('network');
          
          if (isNetworkError) {
            // Don't logout on network errors, just show a warning
            toast({ 
              title: "Network Error", 
              description: "Unable to verify permissions due to network issues. Please check your connection.", 
              variant: "destructive", 
              duration: 5000 
            });
          } else {
            // Only logout for non-network errors
            toast({ title: "Missing Permissions", description: "You lack permissions to access communications.", variant: "destructive", duration: 5000 });
            navigate("/login-page");
          }
        }
      } else {
        // Don't redirect here - let App.tsx global routing handle it
        // This prevents incorrect redirects during network errors
        console.log("TeacherCommunication: No user found, deferring to App.tsx routing");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, toast]);

  const fetchUsers = useCallback(async () => {
    if (!schoolID) return;

    try {
      // Fetch teachers
      const teachersRef = collection(db, `schools/${schoolID}/teachers`);
      const teacherSnapshot = await getDocs(teachersRef);
      const teacherList: User[] = teacherSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          name: `${data.firstName} ${data.lastName}`,
          email: sanitizeHtml(data.email || '', { allowedTags: [] }),
          role: 'school_staff',
          subject: data.subjectsOfExpertise?.join(', ') || 'N/A',
        };
      });

      // Fetch students
      const studentsRef = collection(db, `schools/${schoolID}/students`);
      const allStudentSnapshot = await getDocs(studentsRef);
      const studentList: User[] = [];
      
      for (const docSnap of allStudentSnapshot.docs) {
        const data = docSnap.data() as StudentData;
        let studentEmail = data.email || '';
        
        // If email is missing from document, try to get it from auth user by studentUid
        if (!studentEmail && data.studentUid) {
          console.warn('⚠️ Student missing email in document:', data.studentUid, data.firstName, data.lastName);
          console.log('  Checking if studentUid can be used as auth email lookup...');
          // Note: In a proper setup, you'd store the auth UID or email when creating the student
          // For now, we'll skip students without emails
        }
        
        if (studentEmail) {
          studentList.push({
            id: data.studentUid || docSnap.id,
            name: `${data.firstName} ${data.middleName ? data.middleName + ' ' : ''}${data.lastName}`,
            email: sanitizeHtml(studentEmail, { allowedTags: [] }),
            role: 'student',
          });
        }
      }

      // Fetch guardians from student documents AND invitations
      const guardianEmails = new Set<string>();
      const guardianList: User[] = [];
      const invitationsRef = collection(db, 'invitations');
      const guardianInvites = await getDocs(query(invitationsRef, where('role', '==', 'guardian'), where('schoolID', '==', schoolID), where('status', '==', 'accepted')));
      const guardianAuthIds = new Map<string, string>();
      guardianInvites.forEach(doc => {
        const data = doc.data();
        if (data.invitedUid || data.guardianUid) {
          guardianAuthIds.set(data.email, data.invitedUid || data.guardianUid);
        }
      });

      // Add guardians from INVITES
      for (const docSnap of guardianInvites.docs) {
        const data = docSnap.data();
        if (data.email && !guardianEmails.has(data.email)) {
          // Use guardianName from invitation if present, else fallback to email
          const guardianName = data.guardianName || data.email;
          guardianEmails.add(data.email);
          guardianList.push({
            id: guardianAuthIds.get(data.email) || data.email,
            name: guardianName,
            email: sanitizeHtml(data.email, { allowedTags: [] }),
            role: 'guardian',
            // studentName: For invites, optional—could add logic to link via student if needed
          });
        }
      }

      // Existing: Add from students (deduped)
      allStudentSnapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as StudentData;
        data.guardians?.forEach(guardian => {
          if (guardian.email && !guardianEmails.has(guardian.email)) {
            guardianEmails.add(guardian.email);
            guardianList.push({
              id: guardianAuthIds.get(guardian.email) || guardian.email,
              name: guardian.name,
              email: sanitizeHtml(guardian.email, { allowedTags: [] }),
              role: 'guardian',
              studentName: `${data.firstName} ${data.lastName}`,
            });
          }
        });
      });

      console.log('🛡️ Total guardians (after dedupe):', guardianList.length, guardianList.map(g => ({ name: g.name, email: g.email, studentName: g.studentName })));

      setTeachers(teacherList);
      setStudents(studentList);
      setGuardians(guardianList);

      const totalEveryone = teacherList.length + studentList.length + guardianList.length;
      console.log('📊 Final counts before categories update:', { teachers: teacherList.length, students: studentList.length, guardians: guardianList.length, everyone: totalEveryone });

      setCommunicationCategories(prev => {
        const updated = prev.map(category => ({
          ...category,
          recipientCount: category.id === "everyone" ? totalEveryone :
                          category.id === "staff" ? teacherList.length :
                          category.id === "guardians" ? guardianList.length :
                          category.id === "students" ? studentList.length : 0,
          disabled: category.id === "everyone" ? totalEveryone === 0 :
                    category.id === "staff" ? teacherList.length === 0 :
                    category.id === "guardians" ? guardianList.length === 0 :
                    category.id === "students" ? studentList.length === 0 : true,
        }));
        console.log('📈 Updated categories:', updated.map(c => ({ id: c.id, recipientCount: c.recipientCount, disabled: c.disabled })));
        return updated;
      });
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({ title: "Error", description: "Failed to load users.", variant: "destructive", duration: 5000 });
    }
  }, [schoolID, toast]);

  const fetchConversations = useCallback(() => {
    if (!schoolID || !auth.currentUser?.email) {
      console.log("Skipping fetchConversations: missing schoolID or user email", { schoolID, email: auth.currentUser?.email });
      return () => {};
    }

    const convCollection = `schools/${schoolID}/conversations`;
    const q = query(
      collection(db, convCollection),
      where("allowedEmails", "array-contains", auth.currentUser.email),
      where("schoolID", "==", schoolID),
      orderBy("lastUpdatedAt", "desc"),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const convList: Conversation[] = [];
      let totalUnread = 0;
      
      for (const doc of snapshot.docs) {
        const data = doc.data();
        const hasReceivedMessages = data.lastMessage && data.lastMessage.senderEmail !== auth.currentUser?.email;
        
        // Count actual unread messages from the messages subcollection
        let unreadForConv = 0;
        if (hasReceivedMessages) {
          try {
            const messagesRef = collection(db, `schools/${schoolID}/conversations/${doc.id}/messages`);
            const unreadQuery = query(messagesRef, where('senderEmail', '!=', auth.currentUser?.email || ''));
            const messagesSnapshot = await getDocs(unreadQuery);
            
            messagesSnapshot.forEach((msgDoc) => {
              const msgData = msgDoc.data();
              if (!msgData.readBy?.includes(auth.currentUser?.email || '')) {
                unreadForConv++;
              }
            });
            
            totalUnread += unreadForConv;
          } catch (error) {
            console.error(`Error counting unread for conversation ${doc.id}:`, error);
          }
        }

        convList.push({
          id: doc.id,
          title: data.title || '',
          allowedEmails: data.allowedEmails || [],
          participantRoles: data.participantRoles || [],
          type: data.type || 'group',
          lastMessage: data.lastMessage ? {
            content: data.lastMessage.content || '',
            senderEmail: data.lastMessage.senderEmail || '',
            timestamp: data.lastMessage.timestamp?.toDate()?.toLocaleString() || '',
          } : null,
          lastUpdatedAt: data.lastUpdatedAt?.toDate()?.toLocaleString() || '',
          recipients: data.recipients || '',
          recipientCount: data.recipientCount || 0,
          hasReceivedMessages,
          schoolID: data.schoolID || schoolID,
          unreadCount: unreadForConv,
        });
      }
      setConversations(convList);
      setUnreadCount(totalUnread);
    }, (error) => {
      console.error("Error fetching conversations:", error);
      toast({ title: "Error", description: "Failed to load conversations. Check your permissions.", variant: "destructive", duration: 5000 });
    });

    return unsubscribe;
  }, [schoolID, toast]);

  const fetchMemos = useCallback(() => {
    if (!schoolID) return () => {};
    
    const memosRef = collection(db, `schools/${schoolID}/memos`);
    // Teachers can see memos targeted to 'staff', 'teacher-admin', 'everyone', or specifically to them (if we had that granularity, but for now broad categories)
    // Also they can see memos they created.
    // For simplicity, let's fetch all and filter in UI or fetch relevant ones.
    // Actually, let's fetch all public memos for now or filter by audience.
    // Since Firestore queries are limited with OR, we might need multiple queries or client side filtering if volume is low.
    // Let's fetch all and filter client side for now as volume per school might not be huge yet.
    const q = query(memosRef, orderBy("createdAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const memoList: Memo[] = snapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title,
        description: doc.data().description,
        fileUrl: doc.data().fileUrl,
        fileName: doc.data().fileName,
        fileType: doc.data().fileType,
        targetAudience: doc.data().targetAudience,
        createdAt: doc.data().createdAt?.toDate()?.toLocaleString() || '',
        senderEmail: doc.data().senderEmail,
        recipientCount: doc.data().recipientCount || 0,
        readBy: doc.data().readBy || [],
      }));
      // Filter memos relevant to teacher
      const relevantMemos = memoList.filter(m => 
        ['staff', 'teacher-admin', 'everyone'].includes(m.targetAudience) || 
        m.senderEmail === auth.currentUser?.email
      );
      setMemos(relevantMemos);
    });
    return unsubscribe;
  }, [schoolID]);

  useEffect(() => {
    if (!schoolID || isAuthLoading || !hasValidClaims) return;

    fetchUsers();
    const unsubscribeConv = fetchConversations();
    const unsubscribeMemos = fetchMemos();
    return () => {
      unsubscribeConv();
      unsubscribeMemos();
    };
  }, [schoolID, isAuthLoading, hasValidClaims, fetchUsers, fetchConversations, fetchMemos]);

  const fetchConversationMessages = async (conversationId: string) => {
    setIsLoadingMessages(true);
    try {
      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const q = query(collection(db, messagesCollection), orderBy("timestamp", "desc"), limit(50));
      const snapshot = await getDocs(q);
      const messageList: Message[] = snapshot.docs.map(doc => ({
        id: doc.id,
        content: sanitizeHtml(doc.data().content || '', { allowedTags: [] }),
        senderEmail: sanitizeHtml(doc.data().senderEmail || '', { allowedTags: [] }),
        timestamp: doc.data().timestamp?.toDate()?.toLocaleString() || '',
        readBy: doc.data().readBy || [],
        priority: doc.data().priority || 'normal',
        deliveryMethod: doc.data().deliveryMethod || 'app',
      }));

      const currentUser = auth.currentUser;
      if (currentUser?.email) {
        const currentEmail = currentUser.email;
        const messagesToUpdate = messageList.filter(msg => !msg.readBy.includes(currentEmail));
        for (const msg of messagesToUpdate) {
          try {
            await updateDoc(doc(db, messagesCollection, msg.id), {
              readBy: [...msg.readBy, currentEmail],
            });
          } catch (error) {
            console.error(`Error updating read status for message ${msg.id}:`, error);
          }
        }
      }

      // Show all messages in conversation details view
      setMessages(messageList);
      
      // Mark unread messages as read in Firestore
      const currentUserEmail = auth.currentUser?.email;
      if (currentUserEmail) {
        const batch = writeBatch(db);
        let hasUpdates = false;
        
        for (const msg of messageList) {
          if (msg.senderEmail !== currentUserEmail && !msg.readBy.includes(currentUserEmail)) {
            const msgRef = doc(db, `schools/${schoolID}/conversations/${conversationId}/messages`, msg.id);
            batch.update(msgRef, {
              readBy: arrayUnion(currentUserEmail)
            });
            hasUpdates = true;
          }
        }
        
        if (hasUpdates) {
          await batch.commit();
        }
      }
      
      // Update conversation unread count to 0 and recalculate total
      setConversations(prev => {
        const updated = prev.map(conv => 
          conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
        );
        const newTotal = updated.reduce((sum, conv) => sum + conv.unreadCount, 0);
        setUnreadCount(newTotal);
        return updated;
      });
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast({ title: "Error", description: "Failed to load messages.", variant: "destructive", duration: 5000 });
      setMessages([]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleCategorySelect = (category: CommunicationCategory) => {
    if (category.disabled) {
      toast({ title: "Error", description: `No recipients available for ${category.title}.`, variant: "destructive", duration: 5000 });
      return;
    }
    setSelectedCategory(category);
    setSelectedRecipient("");
    setRecipientSearch("");
    setRecipientType(
      category.id === "staff" ? "teacher" :
      category.id === "guardians" ? "guardian" :
      category.id === "students" ? "student" : null
    );
    setActiveView("compose");
  };

  const handleRecipientSelect = (recipient: User) => {
    setSelectedRecipient(recipient.email);
    setRecipientSearch("");
    setSelectedCategory({
      ...selectedCategory!,
      title: `Send to ${recipient.name}`,
      description: `Direct message to ${recipient.name} (${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'})`,
      recipientCount: 1,
      recipients: `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'}`,
    });
  };

  const handleSendMessage = async () => {
    if (!schoolID || !hasValidClaims || !selectedCategory || !auth.currentUser?.email) {
      toast({ title: "Error", description: "Missing permissions or invalid state.", variant: "destructive", duration: 5000 });
      return;
    }

    if (!subject || !message) {
      toast({ title: "Error", description: "Subject and message are required.", variant: "destructive", duration: 5000 });
      return;
    }

    try {
      const convCollection = `schools/${schoolID}/conversations`;
      let conversationId: string;
      let allowedEmails: string[] = [auth.currentUser.email];
      let participantRoles: string[] = ['school_staff'];
      let convType: '1:1' | 'group' = 'group';
      let recipientCount = selectedCategory.recipientCount;
      let recipients = selectedCategory.recipients;

      if (selectedRecipient && recipientType) {
        const recipient = recipientType === "teacher" ? teachers.find(t => t.email === selectedRecipient) :
                         recipientType === "guardian" ? guardians.find(g => g.email === selectedRecipient) :
                         students.find(s => s.email === selectedRecipient);
        if (!recipient) throw new Error("Selected recipient not found.");
        allowedEmails.push(recipient.email);
        participantRoles.push(recipient.role);
        convType = '1:1';
        recipientCount = 1;
        recipients = `${recipient.name} - ${recipient.role === 'school_staff' ? `Teacher - ${recipient.subject}` : recipient.role === 'guardian' ? `Parent of ${recipient.studentName}` : 'Student'}`;

        const sortedEmails = allowedEmails.sort();
        const convQuery = query(
          collection(db, convCollection),
          where("type", "==", "1:1"),
          where("allowedEmails", "==", sortedEmails),
          where("schoolID", "==", schoolID)
        );
        const convSnapshot = await getDocs(convQuery);
        if (!convSnapshot.empty) {
          conversationId = convSnapshot.docs[0].id;
        } else {
          console.log("Creating new conversation:", {
            title: selectedCategory.title,
            allowedEmails: sortedEmails,
            participantRoles,
            type: convType,
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount,
            schoolID,
          });
          const convDoc = await addDoc(collection(db, convCollection), {
            title: selectedCategory.title,
            allowedEmails: sortedEmails,
            participantRoles,
            type: convType,
            lastMessage: null,
            lastUpdatedAt: serverTimestamp(),
            recipients,
            recipientCount,
            schoolID,
          });
          conversationId = convDoc.id;
          console.log("Conversation created with ID:", conversationId);
        }
      } else {
        if (selectedCategory.id === "everyone") {
          participantRoles = ['admin', 'school_staff', 'guardian', 'student'];
          allowedEmails.push(...teachers.map(t => t.email), ...guardians.map(g => g.email), ...students.map(s => s.email));
        } else if (selectedCategory.id === "staff") {
          participantRoles = ['admin', 'school_staff'];
          allowedEmails.push(...teachers.map(t => t.email));
        } else if (selectedCategory.id === "guardians") {
          participantRoles = ['admin', 'guardian'];
          allowedEmails.push(...guardians.map(g => g.email));
        } else if (selectedCategory.id === "students") {
          participantRoles = ['admin', 'student'];
          allowedEmails.push(...students.map(s => s.email));
        }

        allowedEmails = [...new Set(allowedEmails)];
        console.log("Creating group conversation:", {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: convType,
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount,
          schoolID,
        });
        const convDoc = await addDoc(collection(db, convCollection), {
          title: selectedCategory.title,
          allowedEmails,
          participantRoles,
          type: convType,
          lastMessage: null,
          lastUpdatedAt: serverTimestamp(),
          recipients,
          recipientCount,
          schoolID,
        });
        conversationId = convDoc.id;
        console.log("Conversation created with ID:", conversationId);
      }

      const messagesCollection = `schools/${schoolID}/conversations/${conversationId}/messages`;
      const newMessage = {
        content: sanitizeHtml(`${subject}\n\n${message}`, { allowedTags: [] }),
        senderEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
        readBy: [auth.currentUser.email],
        priority,
        deliveryMethod,
      };

      console.log("Sending message:", newMessage);
      await addDoc(collection(db, messagesCollection), newMessage);
      console.log("Updating conversation:", conversationId);
      await updateDoc(doc(db, convCollection, conversationId), {
        lastMessage: {
          content: newMessage.content,
          senderEmail: auth.currentUser.email,
          timestamp: serverTimestamp(),
        },
        lastUpdatedAt: serverTimestamp(),
      });

      setMessage("");
      setSubject("");
      setPriority("normal");
      setDeliveryMethod("app");
      setSelectedRecipient("");
      setRecipientSearch("");
      setRecipientType(null);
      setActiveView("overview");
      setSelectedCategory(null);
      toast({ title: "Success", description: "Message sent successfully.", duration: 3000 });
    } catch (error: any) {
      console.error("Error sending message:", error);
      toast({
        title: error.code === "permission-denied" ? "Permission Error" : "Error",
        description: error.code === "permission-denied" ? "Insufficient permissions to send message." : error.message || "Failed to send message.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  const handleViewMemo = async (memo: Memo) => {
    setSelectedMemo(memo);
    const userEmail = auth.currentUser?.email;
    if (userEmail && !memo.readBy.includes(userEmail)) {
      try {
        const memoRef = doc(db, `schools/${schoolID}/memos`, memo.id);
        await updateDoc(memoRef, {
          readBy: arrayUnion(userEmail)
        });
        // Optimistic update
        setMemos(prev => prev.map(m => m.id === memo.id ? { ...m, readBy: [...m.readBy, userEmail] } : m));
      } catch (error) {
        console.error("Error marking memo as read:", error);
      }
    }
  };

  const filteredRecipients = recipientType === "teacher" ? teachers.filter(t => t.name.toLowerCase().includes(recipientSearch.toLowerCase()) || t.email.toLowerCase().includes(recipientSearch.toLowerCase()) || t.subject?.toLowerCase().includes(recipientSearch.toLowerCase())) :
                           recipientType === "guardian" ? guardians.filter(g => g.name.toLowerCase().includes(recipientSearch.toLowerCase()) || g.email.toLowerCase().includes(recipientSearch.toLowerCase()) || g.studentName?.toLowerCase().includes(recipientSearch.toLowerCase())) :
                           students.filter(s => s.name.toLowerCase().includes(recipientSearch.toLowerCase()) || s.email.toLowerCase().includes(recipientSearch.toLowerCase()));

  const viewConversationDetails = (conv: Conversation, fromView: "received-messages" | "sent-messages") => {
    setSelectedConversation(conv);
    setReturnView(fromView);
    setMessages([]);
    setShowHistory(false);
    setIsLoadingMessages(true);
    fetchConversationMessages(conv.id);
    setActiveView("conversation-details");
  };

  // Show suspension warning if teacher is suspended
  if (isSuspended) {
    return <SuspensionWarning schoolName={suspendedSchoolName} isLoading={suspensionLoading} />;
  }

  if (isAuthLoading || !schoolID || !hasValidClaims || suspensionLoading) {
    return (
      <SidebarProvider>
        <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
          <div className="hidden md:block md:fixed md:top-0 md:left-0 md:h-screen md:w-64 md:z-50">
            <Teachersidebar />
          </div>
          <div className="flex flex-col flex-1 h-full md:ml-64">
            <Teacherheader />
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-950 flex flex-col">
              <ContentLoader isLoading={true} message="" children={null} />
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900">
        <div className="hidden md:block md:fixed md:top-0 md:left-0 md:h-screen md:w-64 md:z-50">
          <Teachersidebar />
        </div>
        <div className="flex flex-col flex-1 h-full md:ml-64">
          <Teacherheader />
          <main className="flex-1 relative bg-[#FFFFFF] dark:bg-[#020817] overflow-x-hidden">
            <div className="absolute inset-0 overflow-y-auto p-4 sm:p-6 overflow-x-hidden">
              <div className="mb-6 flex justify-center">
                <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => { setActiveTab("messages"); setActiveView("overview"); setSelectedCategory(null); }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "messages" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"}`}
                  >
                    Messages
                  </button>
                  <button
                    onClick={() => { setActiveTab("memos"); setActiveView("overview"); setSelectedCategory(null); }}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === "memos" ? "bg-white dark:bg-gray-700 shadow-sm text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"} flex items-center gap-2`}
                  >
                    Memos
                    {memos.filter(m => !m.readBy.includes(auth.currentUser?.email || '')).length > 0 && (
                      <Badge className="h-5 w-5 p-0 flex items-center justify-center rounded-full text-[10px] bg-red-500 hover:bg-red-600 text-white">
                        {memos.filter(m => !m.readBy.includes(auth.currentUser?.email || '')).length}
                      </Badge>
                    )}
                  </button>
                </div>
              </div>

              {activeTab === "messages" ? (
                <>
              {activeView === "conversation-details" && selectedConversation ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView(returnView)} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back to {returnView === "received-messages" ? "Inbox" : "Sent"}
                  </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  <div className="lg:col-span-3 space-y-6">
                    {/* Email Subject Header */}
                    <div className="bg-white dark:bg-[#020817] p-1">
                        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">{getDisplayTitle()}</h1>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                            <Badge variant="secondary" className="rounded-md font-normal">{selectedConversation.type === '1:1' ? 'Direct Message' : 'Group Announcement'}</Badge>
                            <span>&bull;</span>
                            <span>{selectedConversation.recipients}</span>
                        </div>
                    </div>

                    {/* Message Thread */}
                    <div className="space-y-4">
                        <ContentLoader isLoading={isLoadingMessages} message="">
                        {messages.length === 0 ? (
                          <div className="text-center py-12 bg-white dark:bg-[#020817] rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
                            <p className="text-gray-500">No messages in this conversation.</p>
                          </div>
                        ) : (
                          <>
                            {(() => {
                              const unreadMessages = messages.filter(m => !m.readBy.includes(auth.currentUser?.email || ''));
                              let displayedMessages = messages;
                              
                              if (!showHistory) {
                                if (unreadMessages.length > 0) {
                                  displayedMessages = unreadMessages;
                                } else {
                                  displayedMessages = [messages[0]];
                                }
                              }

                              return (
                                <>
                                  {displayedMessages.map((msg) => {
                                    const senderName = getSenderName(msg.senderEmail);
                                    const isMe = msg.senderEmail === auth.currentUser?.email;
                                    
                                    let displaySubject = "No Subject";
                                    let displayBody = msg.content;
                                    
                                    const parts = msg.content.split('\n\n');
                                    if (parts.length >= 2) {
                                      displaySubject = parts[0];
                                      displayBody = parts.slice(1).join('\n\n');
                                    } else {
                                        const partsSingle = msg.content.split('\n');
                                        if (partsSingle.length >= 2) {
                                            displaySubject = partsSingle[0];
                                            displayBody = partsSingle.slice(1).join('\n');
                                        }
                                    }

                                    return (
                                      <Card key={msg.id} className="border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden bg-white dark:bg-[#020817] mb-6">
                                        <div className="p-0">
                                            <div className="bg-gray-50/80 dark:bg-gray-900/50 p-6 border-b border-gray-100 dark:border-gray-800 space-y-3">
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">To</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{isMe ? selectedConversation.recipients : 'Me'}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">From</span>
                                                    <span className="text-sm font-medium text-gray-900 dark:text-gray-200">{senderName} &lt;{msg.senderEmail}&gt;</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Date</span>
                                                    <span className="text-sm text-gray-600 dark:text-gray-400">{msg.timestamp}</span>
                                                </div>
                                                <div className="grid grid-cols-[60px_1fr] gap-2 items-baseline">
                                                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Subject</span>
                                                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{displaySubject}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="p-6 sm:p-8">
                                                <div className="text-base leading-7 text-gray-700 dark:text-gray-300 font-normal whitespace-pre-wrap break-words font-sans">
                                                    {displayBody}
                                                </div>
                                            </div>
                                            
                                            {isMe && (
                                                <div className="px-6 py-3 bg-gray-50/50 dark:bg-gray-900/30 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                                                    <div className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                                                        {msg.readBy.length > 1 ? (
                                                            <>
                                                                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                                <span className="text-green-700 dark:text-green-500">Read by {msg.readBy.length - 1} recipient(s)</span>
                                                            </>
                                                        ) : (
                                                            <span>Sent</span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                      </Card>
                                    );
                                  })}
                                  
                                  {!showHistory && messages.length > displayedMessages.length && (
                                    <div className="flex justify-center pt-4">
                                      <Button 
                                        variant="outline" 
                                        onClick={() => setShowHistory(true)}
                                        className="flex items-center gap-2 text-gray-500"
                                      >
                                        <History className="h-4 w-4" />
                                        Show Previous Messages ({messages.length - displayedMessages.length})
                                      </Button>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </>
                        )}
                        </ContentLoader>
                    </div>
                  </div>

                  {/* Sidebar Context */}
                  <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
                    <Card className="border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#020817] shadow-sm">
                      <CardHeader className="pb-3 border-b border-gray-100 dark:border-gray-800">
                        <CardTitle className="text-base font-medium">About this conversation</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-4 space-y-4">
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Participants</div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700 dark:text-gray-300">Total Recipients</span>
                                <Badge variant="secondary" className="font-normal">{selectedConversation.recipientCount}</Badge>
                            </div>
                        </div>
                        
                        <div>
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Roles Involved</div>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedConversation.participantRoles.map((role, index) => (
                                <Badge key={index} variant="outline" className="text-xs font-normal capitalize bg-gray-50 dark:bg-gray-900">{role.replace('_', ' ')}</Badge>
                              ))}
                            </div>
                        </div>

                        <div className="pt-2">
                          <Dialog open={showRecipientsModal} onOpenChange={setShowRecipientsModal}>
                            <DialogTrigger asChild>
                              <Button variant="outline" size="sm" className="w-full justify-start text-gray-600 dark:text-gray-300">
                                <UsersIcon className="h-4 w-4 mr-2" />
                                View All Recipients
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                              <DialogHeader>
                                <DialogTitle>Recipients List</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-2">
                                {selectedConversation.allowedEmails.map((email, index) => (
                                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border border-[#E2E8F0] dark:border-[#1E293B]">
                                    <span className="text-sm">{getSenderName(email)}</span>
                                    <Badge variant="outline" className="text-xs border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{email}</Badge>
                                  </div>
                                ))}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : activeView === "received-messages" ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2 self-start">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Received Messages</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Messages sent to you</p>
                  </div>
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Received Messages</CardTitle>
                    <CardDescription>Messages addressed to you</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.hasReceivedMessages).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages received yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {conversations.filter(conv => conv.hasReceivedMessages).map((conv) => {
                        const senderEmail = conv.lastMessage?.senderEmail;
                        const senderName = senderEmail ? getSenderName(senderEmail) : 'Unknown';
                        const fromLabel = conv.type === '1:1' ? `From: ${senderName}` : `From Group: ${conv.title}`;
                        return (
                          <div
                            key={conv.id}
                            className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] cursor-pointer transition-colors"
                            onClick={() => viewConversationDetails(conv, "received-messages")}
                          >
                            <div className="flex items-center justify-between p-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {conv.recipients.includes("All students, staff, and parents") && <Users className="h-4 w-4 text-blue-500" />}
                                  {conv.recipients.includes("Teachers, administrators, and support staff") && <GraduationCap className="h-4 w-4 text-green-500" />}
                                  {conv.recipients.includes("Parents and legal guardians") && <UserCheck className="h-4 w-4 text-purple-500" />}
                                  {conv.recipients.includes("All enrolled students") && <Users className="h-4 w-4 text-orange-500" />}
                                  {conv.type === "1:1" && <UserCheck className="h-4 w-4 text-blue-600" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium truncate">{getConversationListTitle(conv)}</h4>
                                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{fromLabel} • {conv.recipientCount} recipients • {conv.lastUpdatedAt}</p>
                                  {conv.lastMessage && (
                                    <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conv.unreadCount > 0 && (
                                  <Badge variant="destructive" className="text-xs">{conv.unreadCount} New</Badge>
                                )}
                                <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "received-messages"); }} className="flex items-center gap-1">
                                  <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "sent-messages" ? (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 flex items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Sent Messages</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Messages sent by you</p>
                  </div>
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Sent Messages</CardTitle>
                    <CardDescription>Messages sent by you</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No messages sent yet. Start by composing one!</p>
                    ) : (
                      conversations.filter(conv => conv.lastMessage && conv.lastMessage.senderEmail === auth.currentUser?.email).map((conv) => (
                        <div
                          key={conv.id}
                          className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]"
                          onClick={() => viewConversationDetails(conv, "sent-messages")}
                        >
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conv.recipients.includes("All students, staff, and parents") && <Users className="h-4 w-4 text-blue-500" />}
                                {conv.recipients.includes("Teachers, administrators, and support staff") && <GraduationCap className="h-4 w-4 text-green-500" />}
                                {conv.recipients.includes("Parents and legal guardians") && <UserCheck className="h-4 w-4 text-purple-500" />}
                                {conv.recipients.includes("All enrolled students") && <Users className="h-4 w-4 text-orange-500" />}
                                {conv.type === "1:1" && <UserCheck className="h-4 w-4 text-blue-600" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{conv.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">To: {conv.recipients} • {conv.recipientCount} recipients • {conv.lastUpdatedAt}</p>
                                {conv.lastMessage && (
                                  <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="text-xs hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                              <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); viewConversationDetails(conv, "sent-messages"); }} className="flex items-center gap-1">
                                <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : activeView === "compose" && selectedCategory ? (
              <div className="max-w-4xl mx-auto">
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveView("overview")} className="flex items-center gap-2 self-start">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </Button>
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{selectedCategory.title}</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{selectedCategory.recipients}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">{React.createElement(selectedCategory.icon, { className: "h-5 w-5" })} Compose Message</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Subject</label>
                          <Input value={subject} onChange={(e) => setSubject(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Enter message subject..." className="w-full bg-[#FFFFFF] dark:bg-[#020817]" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Priority</label>
                            <Select value={priority} onValueChange={setPriority}>
                              <SelectTrigger className="bg-[#FFFFFF] dark:bg-[#020817]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low Priority</SelectItem>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="high">High Priority</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Delivery Method</label>
                            <Select value={deliveryMethod} onValueChange={setDeliveryMethod}>
                              <SelectTrigger className="bg-[#FFFFFF] dark:bg-[#020817]"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="app">App Notification</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="sms">SMS</SelectItem>
                                <SelectItem value="all">All Methods</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Message</label>
                          <Textarea value={message} onChange={(e) => setMessage(sanitizeHtml(e.target.value, { allowedTags: [] }))} placeholder="Type your message here..." className="min-h-[120px] bg-[#FFFFFF] dark:bg-[#020817]" />
                        </div>
                        <div className="flex justify-end gap-3 mt-4">
                          <Button variant="outline" onClick={() => setActiveView("overview")}>Cancel</Button>
                          <Button onClick={handleSendMessage} disabled={!subject || !message} className="flex items-center gap-2">
                            <Send className="h-4 w-4" /> Send Message
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="space-y-6">
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader><CardTitle>Recipients</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Total Recipients</span>
                          <Badge variant="secondary">{selectedCategory.recipientCount.toLocaleString()}</Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${selectedCategory.color}`} />
                          <span className="text-sm">{selectedCategory.recipients}</span>
                        </div>
                        {(selectedCategory.id === "staff" || selectedCategory.id === "guardians" || selectedCategory.id === "students") && (
                          <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-gray-50 dark:bg-gray-800">
                            <label className="block text-sm font-medium mb-2">Select Specific {recipientType === "teacher" ? "Teacher" : recipientType === "guardian" ? "Guardian" : "Student"} (Optional)</label>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="sm" className="w-full justify-between text-xs">
                                  {selectedRecipient || `Select a ${recipientType === "teacher" ? "teacher" : recipientType === "guardian" ? "guardian" : "student"}`}
                                  <ChevronDown className="h-3 w-3 opacity-50" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-full min-w-[300px] bg-[#FFFFFF] dark:bg-[#020817] border border-[#E2E8F0] dark:border-[#1E293B] shadow-lg z-50">
                                <div className="p-2">
                                  <div className="relative">
                                    <Search className="absolute left-2 top-2.5 h-3 w-3 text-gray-400" />
                                    <Input
                                      placeholder={`Search ${recipientType === "teacher" ? "teachers" : recipientType === "guardian" ? "guardians" : "students"}...`}
                                      value={recipientSearch}
                                      onChange={(e) => setRecipientSearch(sanitizeHtml(e.target.value, { allowedTags: [] }))}
                                      className="pl-7 text-xs h-8 bg-[#FFFFFF] dark:bg-[#020817]"
                                    />
                                  </div>
                                </div>
                                <DropdownMenuSeparator />
                                <div className="max-h-40 overflow-y-auto">
                                  {filteredRecipients.length > 0 ? (
                                    filteredRecipients.map((recipient) => (
                                      <DropdownMenuItem
                                        key={recipient.email}
                                        onClick={() => handleRecipientSelect(recipient)}
                                        className="flex flex-col items-start p-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                                      >
                                        <div className="font-medium text-xs">{recipient.name}</div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400">
                                          {recipientType === "teacher" ? `${recipient.subject} • ${recipient.email}` : 
                                           recipientType === "guardian" ? `Parent of ${recipient.studentName} • ${recipient.email}` : 
                                           `${recipient.email}`}
                                        </div>
                                      </DropdownMenuItem>
                                    ))
                                  ) : (
                                    <DropdownMenuItem disabled className="text-center py-3 text-gray-500 text-xs">
                                      No {recipientType === "teacher" ? "teachers" : recipientType === "guardian" ? "guardians" : "students"} found
                                    </DropdownMenuItem>
                                  )}
                                </div>
                                {selectedRecipient && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedRecipient("");
                                        setRecipientSearch("");
                                        const originalCategory = communicationCategories.find(cat => cat.id === selectedCategory.id)!;
                                        setSelectedCategory({
                                          ...selectedCategory,
                                          title: originalCategory.title,
                                          description: originalCategory.description,
                                          recipients: originalCategory.recipients,
                                          recipientCount: originalCategory.recipientCount,
                                        });
                                      }}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950 text-xs"
                                    >
                                      Clear selection
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            {selectedRecipient && (
                              <div className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-blue-50 dark:bg-blue-950/20 rounded">
                                <div className="flex items-center gap-2">
                                  {recipientType === "teacher" ? (
                                    <GraduationCap className="h-3 w-3 text-green-600" />
                                  ) : recipientType === "guardian" ? (
                                    <UserCheck className="h-3 w-3 text-purple-600" />
                                  ) : (
                                    <Users className="h-3 w-3 text-orange-600" />
                                  )}
                                  <span className="text-xs font-medium text-blue-800 dark:text-blue-200">
                                    Selected: {selectedRecipient}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader><CardTitle>Delivery Preview</CardTitle></CardHeader>
                      <CardContent className="space-y-3">
                        <div className="flex items-center gap-2">
                          {deliveryMethod === "app" && <Bell className="h-4 w-4" />}
                          {deliveryMethod === "email" && <Mail className="h-4 w-4" />}
                          {deliveryMethod === "sms" && <Smartphone className="h-4 w-4" />}
                          {deliveryMethod === "all" && <Users className="h-4 w-4" />}
                          <span className="text-sm capitalize">{deliveryMethod} delivery</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600 dark:text-gray-400">Estimated delivery: 2-5 minutes</span>
                        </div>
                        {priority === "urgent" && <Badge variant="destructive">Urgent Priority</Badge>}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-w-6xl mx-auto">
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1 sm:mb-2">Teacher Communication Center</h1>
                    <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Send announcements and manage communications</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button onClick={() => setActiveView("received-messages")} variant="outline" className="flex items-center gap-2">
                      <Mail className="h-4 w-4" /> View Received Messages
                      {unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-1">{unreadCount}</Badge>
                      )}
                    </Button>
                    <Button onClick={() => setActiveView("sent-messages")} variant="outline" className="flex items-center gap-2">
                      <History className="h-4 w-4" /> View Sent Messages
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {communicationCategories.map((category) => (
                    <Card
                      key={category.id}
                      className={`m-2 border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      style={{ borderLeftColor: category.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : category.color.replace('bg-', '') === 'green-500' ? '#10b981' : category.color.replace('bg-', '') === 'purple-500' ? '#8b5cf6' : '#f97316' }}
                      onClick={() => handleCategorySelect(category)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 ${category.color} rounded-lg`}>{React.createElement(category.icon, { className: "h-5 w-5 text-white" })}</div>
                          <div>
                            <CardTitle className="text-lg">{category.title}</CardTitle>
                            <CardDescription className="text-sm">{category.description}</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400">{category.recipients}</span>
                          <Badge 
                            variant="outline" 
                            className="text-xs w-fit border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-3 py-1"
                          >
                            {selectedCategory?.id === category.id && selectedRecipient ? '1 recipient' : category.recipientCount.toLocaleString() + ' recipients'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
                <Card className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2"><MessageSquare className="h-5 w-5" /> Recent Messages</CardTitle>
                    <CardDescription>Your latest communication activity</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {conversations.length === 0 ? (
                      <p className="text-center text-gray-500 dark:text-gray-400 py-4">No recent messages. Send your first one today!</p>
                    ) : (
                      conversations.slice(0, 5).map((conv) => (
                        <div key={conv.id} className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]">
                          <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h4 className="font-medium truncate">{conv.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">To: {conv.recipients} • {conv.lastUpdatedAt}</p>
                                {conv.lastMessage && (
                                  <p className="text-xs text-gray-500 truncate">Last: {getMessagePreview(conv.lastMessage.content, 50)}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="outline" className="hidden sm:inline-flex border border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-[#181C20]/70 shadow-sm rounded-md px-2 py-0.5">{conv.type}</Badge>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
            </>
            ) : (
              <>
                {selectedMemo ? (
                  <div className="flex flex-col h-full max-w-6xl mx-auto">
                    <div className="mb-4 flex items-center gap-4">
                      <Button variant="outline" size="sm" onClick={() => setSelectedMemo(null)} className="flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" /> Back
                      </Button>
                      <h1 className="text-xl font-bold text-gray-900 dark:text-white">{selectedMemo.title}</h1>
                    </div>
                    
                    <div className="mb-6 bg-white dark:bg-[#020817] border border-gray-200 dark:border-gray-800 rounded-lg p-6 shadow-sm">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                            <GraduationCap className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">School Administration</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">Official Memo</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs font-normal">
                                {selectedMemo.targetAudience === 'everyone' ? 'Public' : 'Internal'}
                              </Badge>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">{selectedMemo.createdAt}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex-1 md:max-w-md">
                           <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Subject: {selectedMemo.title}</h4>
                           <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                             {selectedMemo.description || "No additional description provided."}
                           </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-md overflow-hidden border border-gray-200 dark:border-gray-800 min-h-[600px]">
                      {selectedMemo.fileUrl && (
                        <iframe src={selectedMemo.fileUrl} className="w-full h-full" title={selectedMemo.title} />
                      )}
                    </div>
                  </div>
                ) : activeMemoCategory ? (
                  <div className="max-w-6xl mx-auto">
                    <div className="mb-4 flex items-center gap-4">
                      <Button variant="outline" size="sm" onClick={() => setActiveMemoCategory(null)} className="flex items-center gap-2">
                        <ArrowLeft className="h-4 w-4" /> Back to Memos
                      </Button>
                      <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                          {memoCategories.find(c => c.id === activeMemoCategory)?.title}
                        </h1>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {memoCategories.find(c => c.id === activeMemoCategory)?.description}
                        </p>
                      </div>
                    </div>
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardContent className="space-y-4 p-4">
                        {memos.filter(m => m.targetAudience === activeMemoCategory).length === 0 ? (
                          <p className="text-center text-gray-500 py-8">No memos in this category.</p>
                        ) : (
                          memos.filter(m => m.targetAudience === activeMemoCategory).map((memo) => (
                            <div key={memo.id} className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B] transition-colors">
                              <div className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <FileText className="h-8 w-8 text-blue-500 flex-shrink-0 p-1.5 bg-blue-50 dark:bg-blue-900/20 rounded-lg" />
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-medium truncate text-base">{memo.title}</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                      {memo.description || "No description"} • {memo.createdAt}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => handleViewMemo(memo)}>
                                    <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                                  </Button>
                                  <a href={memo.fileUrl} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                                      <Download className="h-3 w-3" /> <span className="hidden sm:inline">Download</span>
                                    </Button>
                                  </a>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="max-w-6xl mx-auto">
                    <div className="mb-4 sm:mb-6 space-y-4">
                      <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2">Memo Management</h1>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">View official memos</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8">
                      {memoCategories.map((category) => {
                        const count = memos.filter(m => m.targetAudience === category.id && !m.readBy.includes(auth.currentUser?.email || '')).length;
                        return (
                          <Card
                            key={category.id}
                            className={`border border-[#E2E8F0] dark:border-[#1E293B] hover:shadow-lg transition-all cursor-pointer border-l-4 bg-[#FFFFFF] dark:bg-[#020817] ${category.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                            style={{ borderLeftColor: category.color.replace('bg-', '') === 'blue-500' ? '#3b82f6' : category.color.replace('bg-', '') === 'green-500' ? '#10b981' : category.color.replace('bg-', '') === 'purple-500' ? '#8b5cf6' : '#f97316' }}
                            onClick={() => setActiveMemoCategory(category.id)}
                          >
                            <CardHeader className="p-3 sm:pb-3">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className={`p-1.5 sm:p-2 ${category.color} rounded-lg`}>
                                  {React.createElement(category.icon, { className: "h-4 w-4 sm:h-5 sm:w-5 text-white" })}
                                </div>
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <CardTitle className="text-sm sm:text-lg line-clamp-1">{category.title}</CardTitle>
                                    {count > 0 && (
                                      <Badge className="bg-red-500 hover:bg-red-600 text-white border-none">
                                        {count}
                                      </Badge>
                                    )}
                                  </div>
                                  <CardDescription className="text-xs sm:text-sm line-clamp-2">{category.description}</CardDescription>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent className="p-3">
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-1">{category.recipients}</span>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Recent Memos</CardTitle>
                        <CardDescription>Recently uploaded memos (Last 5)</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {memos.length === 0 ? (
                          <p className="text-center text-gray-500 dark:text-gray-400 py-4">No memos uploaded yet.</p>
                        ) : (
                          memos.slice(0, 5).map((memo) => (
                            <div key={memo.id} className="m-2 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg hover:bg-gray-50 dark:hover:bg-[#2C2C3B]">
                              <div className="flex items-center justify-between p-4">
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-medium truncate">{memo.title}</h4>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">Target: {memo.targetAudience} • {memo.createdAt}</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <Button variant="outline" size="sm" className="flex items-center gap-1" onClick={() => handleViewMemo(memo)}>
                                    <Eye className="h-3 w-3" /> <span className="hidden sm:inline">View</span>
                                  </Button>
                                  <a href={memo.fileUrl} target="_blank" rel="noopener noreferrer">
                                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                                      <Download className="h-3 w-3" /> <span className="hidden sm:inline">Download</span>
                                    </Button>
                                  </a>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </div>
                )}
              </>
            )}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};export default TeacherCommunication;